import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, Paper, ThemeProvider, createTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useResendCodeMutation, useVerifyCodeMutation } from './services/authenticationApi';
import { jwtDecode } from 'jwt-decode';
import { useDispatch } from 'react-redux';
import { isLoginFunc } from '../../store/slices/login';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuthRedirect } from '../../hooks/use-auth-redirect';
import { getTokenRoles } from '../../utils/jwt-roles';
import { resetUnViewedBadLoginsSession } from '../transactions/components/UnViewedBadLoginsModal';

// Light theme for login pages only
const lightTheme = createTheme({
  palette: {
    mode: 'light',
  },
});

const GradientBackground = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  zIndex: 0,
  pointerEvents: 'none',
  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
  clipPath: 'polygon(0 0, 51% 0, 39% 100%, 0% 100%)',
  '@media (max-width: 900px)': {
    clipPath: 'none'
  }
}));

const VerifyCodePage = () => {
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyCode, { isLoading: isLoadingVerify }] = useVerifyCodeMutation();
  const [resendCode, { isLoading: isResending }] = useResendCodeMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Force light mode background
  useEffect(() => {
    document.body.style.backgroundColor = '#ffffff';
    document.documentElement.style.backgroundColor = '#ffffff';
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, []);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const requestInProgressRef = useRef(false);

  // Eğer geçerli accessToken varsa doğrulama sayfasına erişimi engelle ve anasayfaya yönlendir
  const { isChecking } = useAuthRedirect();

  // Geri sayım timer'ı; kontrol devam ederken çalışmasın
  useEffect(() => {
    if (isChecking || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, isChecking]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const submitVerification = async (code: string) => {
    // Enter / multi-click / auto-submit aynı anda gelse bile tek istek
    if (requestInProgressRef.current || isLoadingVerify || isSubmitting) return;
    if (code.length !== 6) return;

    requestInProgressRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await verifyCode(code).unwrap();
      if (response.success) {
        // Login sonrası 2FA verify cevabından accessToken ve refreshToken'ı al
        if (response.data?.token) {
          localStorage.setItem('accessToken', response.data.token);
          localStorage.removeItem('verifyToken');
        }
        if (response.data?.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
        resetUnViewedBadLoginsSession();

        // Token'daki role'e göre yönlendirme yap: ChangeExpirePassword ise şifre değiştirme sayfasına
        let mustChangePassword = false;
        if (response.data?.token) {
          try {
            const decodedToken: any = jwtDecode(response.data.token);
            const roles = getTokenRoles(decodedToken);
            mustChangePassword =
              roles.includes('ChangeExpirePassword') || roles.includes('ChangePassword');
          } catch {
            // Token decode edilemezse varsayılan yönlendirme kullanılır
          }
        }

        setIsFadingOut(true);
        setTimeout(() => {
          if (mustChangePassword) {
            navigate('/authentication/change-pass', { replace: true });
          } else {
            navigate('/dashboard/transactions');
            dispatch(isLoginFunc(true));
          }
        }, 400); // 400ms fade süresi
        // Başarılı durumda lock'u açma - sayfa değişecek, tekrar istek atılmasın
        return;
      }

      setIsSubmitting(false);
      requestInProgressRef.current = false;
    } catch (error: any) {
      setIsSubmitting(false);
      requestInProgressRef.current = false;

      const status = error?.status ?? error?.originalStatus;
      if (status === 401 || status === 403) {
        navigate('/login', { replace: true });
        return;
      }
      // Hata middleware tarafından gösterilecek
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitVerification(verificationCode);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 6);
    setVerificationCode(value);

    if (value.length === 6) {
      void submitVerification(value);
    }
  };

  const handleResendCode = async () => {
    try {
      const response = await resendCode().unwrap();
      if (response.success) {
        setTimeLeft(180); // Reset timer
        enqueueSnackbar('Yeni kod gönderildi', { variant: 'success' });
      }
    } catch (error: any) {
      const status = error?.status ?? error?.originalStatus;
      if (status === 401 || status === 403) {
        navigate('/login', { replace: true });
        return;
      }
    }
  };

  if (isChecking) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
        }}
      >
        <CircularProgress sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        className={isFadingOut ? 'fade-out' : ''}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'safe center',
          width: '100vw',
          height: '100vh',
          maxHeight: '100vh',
          minHeight: 0,
          py: { xs: 1.5, sm: 2 },
          px: { xs: 1.5, sm: 2 },
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#ffffff !important'
        }}
      >
        <GradientBackground />

        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={3}
          sx={{
            width: {
              xs: '80%',
              xl: '51.4%'
            },
            maxWidth: '984px',
            flexShrink: 0,
            padding: {
              xs: 0,
              md: 5
            },
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row'
            },
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 20,
            bgcolor: 'rgba(255, 255, 255, 0.5)',
            borderRadius: '35px',
            border: '1.2px solid rgba(0, 0, 0, 0.1)',
            overflow: 'visible',
            gap: {
              xs: 2,
              md: 5
            }
          }}
        >
          <Box
            sx={{
              width: {
                xs: '60%',
                md: '41%'
              },
              p: 2.5,
              color: 'white'
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/assets/images/logo/newlogo.png`}
              alt="Logo"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}
            />
          </Box>

          <Box
            sx={{
              width: { xs: '100%', md: '59%' },
              bgcolor: { xs: 'white', md: 'transparent' },
              p: { xs: 2.5, md: 0 },
              borderBottomLeftRadius: { xs: '35px', md: 0 },
              borderBottomRightRadius: { xs: '35px', md: 0 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: {
                  xs: '16px',
                  md: '22px'
                },
                color: 'black'
              }}
            >
              Doğrulama Kodu
            </Typography>

            <Box sx={{ width: { xs: '100%', md: '80%' } }}>
              <TextField
                value={verificationCode}
                onChange={handleCodeChange}
                label="Doğrulama Kodunu Giriniz"
                required
                fullWidth
                placeholder="Kodu Giriniz..."
                disabled={isLoadingVerify || isSubmitting}
                inputProps={{
                  minLength: 6,
                  maxLength: 6,
                  autoComplete: 'off',
                  inputMode: 'numeric'
                }}
              />
              {timeLeft <= 0 ? (
                <Button
                  onClick={handleResendCode}
                  disabled={isResending}
                  sx={{
                    mt: 1,
                    width: '100%',
                    color: '#1e3c72',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(30, 60, 114, 0.08)'
                    }
                  }}
                >
                  {isResending ? 'Gönderiliyor...' : 'Tekrar Kod Gönder'}
                </Button>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    color: timeLeft <= 30 ? 'error.main' : 'text.secondary'
                  }}
                >
                  Kalan Süre: {formatTime(timeLeft)}
                </Typography>
              )}
            </Box>

            <Button
              type="submit"
              fullWidth
              disabled={isLoadingVerify || isSubmitting}
              sx={{
                width: { xs: '100%', md: '80%' },
                height: { xs: '48px' },
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                borderColor: '#1e3c72',
                borderRadius: '12px',
                color: 'white',
                fontSize: '15px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a3569 0%, #25478a 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(30, 60, 114, 0.35)'
                },
                '&:disabled': {
                  background: 'rgba(30, 60, 114, 0.3)',
                  color: 'rgba(255, 255, 255, 0.6)'
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {(isLoadingVerify || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : 'Gönder'}
            </Button>
          </Box>
        </Paper>
      </Box>
      <style>{`
        .fade-out {
          animation: fadeOutVerify 0.4s forwards;
        }
        @keyframes fadeOutVerify {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `}</style>
    </ThemeProvider>
  );
};

export default VerifyCodePage;
